import { createSlice } from "@reduxjs/toolkit";
import ConversationModel from "model/Conversation.model";
import {
  addConversationToTheBeginningReducer,
  appendANewConversationReducer,
  appendMoreConversationsReducer,
  mergeConversationListReducer,
  setConversationListReducer,
  setCurrentFilterChainIdReducer,
  setCurrentPromptCycleReducer,
  setCurrentTimelineObjectReducer,
  setCurrentVisualizationReducer,
  setFileStatusReducer,
  setFilterChainEdgesReducer,
  setFilterChainNameReducer,
  setFilterChainNodesReducer,
  setIsChatHistoryLoadingReducer,
  setIsFilterChainLoadingReducer,
  setIsLastMessageInProgressReducer,
  setIsMessageStreamingReducer,
  setIsSSEActiveReducer,
  setIsStartedAnalyzingReducer,
  setReceivedSummaryReducer,
  setSelectedConversationReducer,
  setSessionContextFileReducer,
  setSessionSummaryReducer,
  setShowChatbarReducer,
  toggleChatbarReducer,
  updateConversationReducer,
  updateNewConversationReducer,
} from "./reducer/Session.reducer";
import { IVisualization } from "interface/component";
import { SessionTimelinePointInfo } from "model/SessionTimelinePointInfo.model";
import MessageModel from "model/Message.model";
import GetSessionSummaryResponse from "model/api/GetSessionSummaryResponse";

export interface SessionState {
  conversations: ConversationModel[];
  selectedConversation: ConversationModel;
  showChatbar: boolean;
  isMessageStreaming: boolean;
  isLastMessageInProgress: boolean;
  isChatHistoryLoading: boolean;
  isStartedAnalyzing: boolean;
  currentFilterChainId: string;
  isFilterChainLoading: boolean;
  filterChainNodes: any[];
  filterChainEdges: any[];
  filterChainName: string | null;
  currentVisualization?: IVisualization | null;
  currentTimelineObject?: SessionTimelinePointInfo | null;
  receivedSummary?: boolean;
  currentPromptCycle?: MessageModel | null;
  isSSEActive: boolean;
  sessionSummaryObj: GetSessionSummaryResponse | null;
  chainOfThoughtContainerRef?: any;
}

const initialState: SessionState = {
  conversations: [],
  selectedConversation: new ConversationModel(),
  showChatbar: true,
  isMessageStreaming: false,
  isLastMessageInProgress: false,
  isChatHistoryLoading: false,
  isStartedAnalyzing: false,
  currentFilterChainId: "",
  isFilterChainLoading: false,
  filterChainNodes: [],
  filterChainEdges: [],
  filterChainName: null,
  currentVisualization: null,
  currentTimelineObject: null,
  receivedSummary: false,
  currentPromptCycle: null,
  isSSEActive: false,
  sessionSummaryObj: null,
  chainOfThoughtContainerRef: null,
};

const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    setSelectedConversation: setSelectedConversationReducer,
    setConversationList: setConversationListReducer,
    mergeConversationList: mergeConversationListReducer,
    addConversationToTheBeginning: addConversationToTheBeginningReducer,
    appendANewConversation: appendANewConversationReducer,
    appendMoreConversations: appendMoreConversationsReducer,
    updateNewConversation: updateNewConversationReducer,
    updateConversation: updateConversationReducer,
    setShowChatbar: setShowChatbarReducer,
    toggleChatbar: toggleChatbarReducer,
    setIsMessageStreaming: setIsMessageStreamingReducer,
    setIsLastMessageInProgress: setIsLastMessageInProgressReducer,
    setIsChatHistoryLoading: setIsChatHistoryLoadingReducer,
    setIsStartedAnalyzing: setIsStartedAnalyzingReducer,
    setCurrentFilterChainId: setCurrentFilterChainIdReducer,
    setIsFilterChainLoading: setIsFilterChainLoadingReducer,
    setFilterChainNodes: setFilterChainNodesReducer,
    setFilterChainEdges: setFilterChainEdgesReducer,
    setFilterChainName: setFilterChainNameReducer,
    setCurrentVisualization: setCurrentVisualizationReducer,
    setCurrentTimelineObject: setCurrentTimelineObjectReducer,
    setReceivedSummary: setReceivedSummaryReducer,
    setCurrentPromptCycle: setCurrentPromptCycleReducer,
    setIsSSEActive: setIsSSEActiveReducer,
    setSessionContextFile: setSessionContextFileReducer,
    setSessionSummaryObj: setSessionSummaryReducer,
    setFileStatus: setFileStatusReducer,
    setChainOfThoughtContainerRef(state, action) {
      return {
        ...state,
        chainOfThoughtContainerRef: {
          ...state.chainOfThoughtContainerRef,
          ...action.payload,
        },
      };
    },
    clearChainOfThoughtContainerRef(state) {
      return {
        ...state,
        chainOfThoughtContainerRef: {
          ...state.chainOfThoughtContainerRef,
          current: null,
        },
      };
    },
  },
});

export const {
  setConversationList,
  mergeConversationList,
  addConversationToTheBeginning,
  appendANewConversation, 
  appendMoreConversations,
  updateNewConversation,
  updateConversation,
  setSelectedConversation,
  setShowChatbar,
  toggleChatbar,
  setIsMessageStreaming,
  setIsLastMessageInProgress,
  setIsChatHistoryLoading,
  setIsStartedAnalyzing,
  setCurrentFilterChainId,
  setIsFilterChainLoading,
  setFilterChainEdges,
  setFilterChainNodes,
  setFilterChainName,
  setCurrentVisualization,
  setCurrentTimelineObject,
  setReceivedSummary,
  setCurrentPromptCycle,
  setIsSSEActive,
  setSessionContextFile,
  setSessionSummaryObj,
  setFileStatus,
  setChainOfThoughtContainerRef,
  clearChainOfThoughtContainerRef,
} = sessionSlice.actions;

export default sessionSlice.reducer;
